import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

/**
 * Grid component that implements our grid system using CSS classes
 * This component uses the CSS classes defined in the unified grid system
 * in theme/layouts/_grid-system.scss for better consistency and maintainability
 */
const Grid = ({
  children,
  className,
  cols = 12,
  gap,
  center,
  debug = false,
  smallScreenBreakpoint,
  smallScreenCols,
  as: Component = 'div',
  ...rest
}) => {
  // Determine the appropriate CSS class based on cols prop
  let gridClassName;
  let customStyles = {};

  if (typeof cols === 'number') {
    gridClassName = 'grid'; // Standard 12-column grid
  } else if (cols === 'auto-fit') {
    gridClassName = 'grid-auto-fit';
  } else if (cols === '2-cols') {
    gridClassName = 'grid-2-cols';
  } else if (cols === '3-cols') {
    gridClassName = 'grid-3-cols';
  } else if (cols === 'main-sidebar') {
    gridClassName = 'grid-main-sidebar';
  } else if (
    typeof cols === 'string' &&
    (cols.includes('fr') || cols.includes('px') || cols.includes('%'))
  ) {
    // Handle custom grid template columns with direct CSS
    gridClassName = 'grid';
    customStyles.gridTemplateColumns = cols;
  } else {
    gridClassName = 'grid'; // Default to standard grid
  }

  // Set CSS variables for responsive behavior if specified
  if (smallScreenBreakpoint) {
    customStyles['--small-screen-breakpoint'] = smallScreenBreakpoint;
  }

  if (smallScreenCols) {
    customStyles['--small-screen-cols'] = smallScreenCols;
  }

  const finalClassName = cx(
    gridClassName,
    {
      'grid-center': center,
      debug: debug,
      'grid-custom-responsive': smallScreenCols !== undefined,
    },
    className,
  );

  // Combine all styles
  const style = {
    ...(gap ? { gap } : {}),
    ...customStyles,
    ...rest.style,
  };

  // Clean up the rest props to avoid passing non-standard HTML attributes to DOM
  const {
    style: _,
    smallScreenBreakpoint: _sb,
    smallScreenCols: _sc,
    ...restWithoutProps
  } = rest;

  return (
    <Component className={finalClassName} style={style} {...restWithoutProps}>
      {children}
    </Component>
  );
};

Grid.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  cols: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf(['auto-fit', '2-cols', '3-cols', 'main-sidebar']),
    PropTypes.string, // Allow any string for custom grid templates
  ]),
  gap: PropTypes.string,
  center: PropTypes.bool,
  debug: PropTypes.bool,
  smallScreenBreakpoint: PropTypes.string, // Breakpoint where small screen behavior begins
  smallScreenCols: PropTypes.string, // Columns layout for small screens
  as: PropTypes.elementType,
};

export default Grid;
